import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AdminAppointmentsComponent } from './admin-appointments.component';
import { StaffAppointmentsComponent } from './staff-appointments.component';
import { RoleService } from '../services/role.service';
import { Subject } from 'rxjs';
import { destroyRoleWrapper, initializeRoleWrapper } from '../services/role-wrapper.utils';

/**
 * Wrapper component that displays either the admin or staff appointments component based on the current user's role.
 * Purpose: Acts as a simple role-based wrapper that conditionally renders <app-admin-appointments> for admin users or <app-staff-appointments> for non-admin users.
 * Behavior: Uses an injected RoleService to determine the current user's role and exposes an internal isAdminUser flag. Manages subscription lifecycle via a private destroy$ Subject and delegates UI and event handling to the child components.
 * Key methods:
 *   - constructor(roleService: RoleService): injects the RoleService dependency.
 *   - ngOnInit(): initializes role detection by calling initializeRoleWrapper(this, this.roleService, this.destroy$) to set isAdminUser and start any required subscriptions.
 *   - ngOnDestroy(): cleans up subscriptions and resources by calling destroyRoleWrapper(this.destroy$).
 * Lifecycle hooks:
 *   - OnInit: sets up role wrapper and subscription handling.
 *   - OnDestroy: tears down subscriptions to prevent memory leaks.
 * @component
 * @selector app-appointments-wrapper
 * @example
 *   <app-appointments-wrapper></app-appointments-wrapper>
 * @input {none} none - This component does not accept any @Input properties; it determines displayed content from RoleService.
 * @output {none} none - This component does not emit any @Output events; child components handle their own outputs.
 */
@Component({
  selector: 'app-appointments-wrapper',
  standalone: true,
  imports: [CommonModule, AdminAppointmentsComponent, StaffAppointmentsComponent],
  template: `
    <!-- Admin Appointments Component -->
    <app-admin-appointments *ngIf="isAdminUser"></app-admin-appointments>

    <!-- Staff Appointments Component -->
    <app-staff-appointments *ngIf="!isAdminUser"></app-staff-appointments>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }
    `,
  ],
})
export class AppointmentsWrapperComponent implements OnInit, OnDestroy {
  isAdminUser = false;
  private destroy$ = new Subject<void>();

  constructor(private roleService: RoleService) {}

  ngOnInit(): void {
    initializeRoleWrapper(this, this.roleService, this.destroy$);
  }

  ngOnDestroy(): void {
    destroyRoleWrapper(this.destroy$);
  }
}
